import { createCheckboxTokens } from '../tokens/components.js';
import { SEMANTIC, type SemanticTokens } from '../tokens/semantic.js';

/**
 * Checkbox Component Configuration
 *
 * The DS checkbox: a 20px box, radius 5, a 1.5px Border/default edge on Surface/ground, filled
 * with the secondary ink and an 11px white check once ticked. PrimeNG's own check icon is hidden
 * and the check is drawn from primeicons, so the box never depends on PrimeNG's SVG.
 */
export function createCheckbox(s: SemanticTokens) {
  const checkboxTokens = createCheckboxTokens(s);
  return {
    root: {
      borderRadius: checkboxTokens.base.radius,
      width: checkboxTokens.base.size,
      height: checkboxTokens.base.size,
    },
    icon: {
      size: checkboxTokens.base.iconSize,
      color: checkboxTokens.checkColor,
      checkedColor: checkboxTokens.checkColor,
      checkedHoverColor: checkboxTokens.checkColor,
      disabledColor: checkboxTokens.disabled,
    },
    css: () => `
    .p-checkbox {
      width: ${checkboxTokens.base.size};
      height: ${checkboxTokens.base.size};
      position: relative;
    }

    .p-checkbox .p-checkbox-box {
      width: ${checkboxTokens.base.size};
      height: ${checkboxTokens.base.size};
      border: ${checkboxTokens.base.borderWidth} solid ${checkboxTokens.secondary.unchecked};
      border-radius: ${checkboxTokens.base.radius};
      background: ${checkboxTokens.background};
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all ${checkboxTokens.base.transition};
    }

    /* Hide default PrimeNG check icon - using :root for specificity */
    :root .p-checkbox .p-checkbox-box .p-checkbox-icon,
    :root .p-checkbox .p-checkbox-box svg,
    :root .p-checkbox .p-checkbox-box .p-icon,
    :root .p-checkbox .p-checkbox-box checkicon {
      display: none !important;
      opacity: 0;
      visibility: hidden;
    }

    :root .p-checkbox .p-checkbox-box::before {
      content: "\\e909";
      font-family: 'primeicons';
      font-size: ${checkboxTokens.base.iconSize};
      line-height: 1;
      color: transparent;
      display: block;
    }

    .p-checkbox:not(.p-disabled):hover .p-checkbox-box {
      border-color: ${checkboxTokens.secondary.hover};
    }

    .p-checkbox.p-checkbox-checked .p-checkbox-box,
    .p-checkbox.p-highlight .p-checkbox-box,
    .p-checkbox[data-p-checked="true"] .p-checkbox-box {
      background: ${checkboxTokens.secondary.checked};
      border-color: ${checkboxTokens.secondary.checked};
    }

    .p-checkbox.p-checkbox-checked .p-checkbox-box::before,
    .p-checkbox.p-highlight .p-checkbox-box::before,
    .p-checkbox[data-p-checked="true"] .p-checkbox-box::before {
      color: ${checkboxTokens.checkColor};
    }

    .p-checkbox.p-checkbox-checked:not(.p-disabled):hover .p-checkbox-box,
    .p-checkbox.p-highlight:not(.p-disabled):hover .p-checkbox-box,
    .p-checkbox[data-p-checked="true"]:not(.p-disabled):hover .p-checkbox-box {
      background: ${checkboxTokens.secondary.checkedHover};
      border-color: ${checkboxTokens.secondary.checkedHover};
    }

    /* Disabled state */
    .p-checkbox.p-disabled .p-checkbox-box,
    .p-checkbox-disabled .p-checkbox-box {
      background: ${checkboxTokens.disabled};
      border-color: ${checkboxTokens.disabled};
    }

    /* Invalid state */
    .p-checkbox.p-invalid .p-checkbox-box {
      border-color: ${checkboxTokens.invalid};
    }

    /* Focus ring */
    .p-checkbox:has(.p-checkbox-input:focus-visible) .p-checkbox-box {
      outline: none;
      box-shadow: 0 0 0 2px ${checkboxTokens.secondary.focus};
    }
  `,
  };
}
export const checkbox = createCheckbox(SEMANTIC);
